/**
 * La hiérarchie des rôles, leurs libellés et leurs couleurs.
 *
 * Séparés du badge qui les affiche : un fichier qui exporte à la fois un
 * composant et des constantes casse le rechargement à chaud, et l'état de
 * la page repart de zéro à chaque modification.
 *
 * Reflète la même hiérarchie et les mêmes dégradés que le service
 * RoleBadgeConfig côté Symfony.
 */
export interface RoleBadgeConfig {
  label: string;
  icon: string;
  from: string;
  to: string;
  darkFrom: string;
  darkTo: string;
}

export const ROLE_BADGE_MAP: Record<string, RoleBadgeConfig> = {
  ROLE_SUPER_ADMIN: {
    label: 'Super Admin',
    icon: '👑',
    from: 'from-red-500',
    to: 'to-orange-500',
    darkFrom: 'dark:from-red-600',
    darkTo: 'dark:to-orange-600',
  },
  ROLE_ADMIN: {
    label: 'Administrateur',
    icon: '⚙️',
    from: 'from-violet-500',
    to: 'to-indigo-500',
    darkFrom: 'dark:from-violet-600',
    darkTo: 'dark:to-indigo-600',
  },
  ROLE_BUREAU: {
    label: 'Bureau',
    icon: '🏛️',
    from: 'from-blue-500',
    to: 'to-cyan-500',
    darkFrom: 'dark:from-blue-600',
    darkTo: 'dark:to-cyan-600',
  },
  ROLE_VOLUNTEER: {
    label: 'Benevole',
    icon: '🤝',
    from: 'from-green-500',
    to: 'to-emerald-500',
    darkFrom: 'dark:from-green-600',
    darkTo: 'dark:to-emerald-600',
  },
  ROLE_SPONSOR: {
    label: 'Sponsor',
    icon: '🏅',
    from: 'from-amber-500',
    to: 'to-yellow-500',
    darkFrom: 'dark:from-amber-600',
    darkTo: 'dark:to-yellow-600',
  },
  ROLE_VISITOR: {
    label: 'Visiteur',
    icon: '👁️',
    from: 'from-gray-400',
    to: 'to-slate-500',
    darkFrom: 'dark:from-gray-600',
    darkTo: 'dark:to-slate-700',
  },
};

const ROLE_PRIORITY = [
  'ROLE_SUPER_ADMIN',
  'ROLE_ADMIN',
  'ROLE_BUREAU',
  'ROLE_VOLUNTEER',
  'ROLE_SPONSOR',
  'ROLE_VISITOR',
];

/**
 * Traduit les anciens noms de roles vers leur valeur actuelle, afin qu'une
 * session ouverte avant la migration continue d'afficher le bon badge.
 */
export function normalizeRole(role: string): string {
  return role === 'ROLE_BENEVOLE' ? 'ROLE_VOLUNTEER' : role;
}

export function getHighestRole(roles: string[]): string | null {
  const normalized = roles.map(normalizeRole);
  for (const priorityRole of ROLE_PRIORITY) {
    if (normalized.includes(priorityRole)) {
      return priorityRole;
    }
  }
  return null;
}

export function getRoleConfig(role: string | null): RoleBadgeConfig | null {
  if (!role) return null;
  return ROLE_BADGE_MAP[normalizeRole(role)] ?? null;
}
